import React, { useState, useCallback } from 'react';
import { ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { closeJob, publishJob, archiveJob, getJobStatusInfo } from '../../Helpers/jobHelper';
import ConfigureEmailModal from '../Modals/configureEmailModal';
import { JobStatus } from '../../Helpers/enums';
import { AutomatedEmailSending } from '../../Helpers/enums';
import { updateCompanyEmailSettingsLocal } from '../../Helpers/companyHelper';
import { getParsedEmailSetting } from '../../Helpers/companyHelper';
import { isCompanyVerified } from '../../Helpers/companyHelper';
import useCompanyStore from '../../../../stores/companyStore';

const JobStatusButtons = ({ job, btnSize = 'xs', onStatusRefresh, language }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfigureEmailModalOpen, setIsConfigureEmailModalOpen] = useState(false);

  const company = useCompanyStore((state) => state.company);
  const fetchCompany = useCompanyStore((state) => state.fetchCompany);

  const [selectedEmail, setSelectedEmail] = useState();
  const { t } = useTranslation('translations');

  const toggle = useCallback(() => {
    composeSendEmailToAllNewApplicantsWhenJobClosesOrArchive();
    setIsOpen(!isOpen);
  }, [isOpen]);

  const JobStatusButton = ({ job, color, status, children }) => (
    <ButtonDropdown isOpen={isOpen} toggle={toggle}>
      <DropdownToggle caret size={btnSize} outline color={color}>
        {status}
      </DropdownToggle>
      <DropdownMenu>{children}</DropdownMenu>
      <TooltipIcon id={`infoScore-${job.id}`} jobStatus={job.jobStatus} />
    </ButtonDropdown>
  );

  const TooltipIcon = ({ id, jobStatus }) => (
    <>
      &nbsp;
      <em id={id} style={{ cursor: 'pointer' }} className="fa mr-2 fas fa-info-circle text-muted" />
      <UncontrolledTooltip delay={{ show: 300, hide: 0 }} placement="left" target={id}>
        {getJobStatusInfo(jobStatus, language)}
      </UncontrolledTooltip>
    </>
  );

  const handleAction = useCallback(
    (actionFunc) => {
      if (actionFunc.name === 'closeJob' || actionFunc.name === 'publishJob') {
        if (!isCompanyVerified(t)) {
          return;
        }
      }

      actionFunc(job, t, onStatusRefresh, toggleIsConfigureEmailModalOpen);
    },
    [job, t, onStatusRefresh]
  );

  const toggleIsConfigureEmailModalOpen = () => {
    setIsConfigureEmailModalOpen(!isConfigureEmailModalOpen);
  };

  const composeSendEmailToAllNewApplicantsWhenJobClosesOrArchive = () => {
    const affectedEmail = getParsedEmailSetting(
      AutomatedEmailSending.SendEmailToAllNewApplicantsWhenJobClosesOrArchives,
      company
    );

    setSelectedEmail({
      id: AutomatedEmailSending.SendEmailToAllNewApplicantsWhenJobClosesOrArchives,
      emailSubject: affectedEmail?.EmailSubject,
      emailContent: affectedEmail?.EmailContent,
    });
  };

  const jobStatus = job.jobStatus;
  const renderButton = () => {
    switch (jobStatus) {
      case JobStatus.Published:
        return (
          <JobStatusButton job={job} color="success" status={t('jobs.PUBLISHED')}>
            <DropdownItem onClick={() => handleAction(archiveJob)}>
              <b>{t('jobs.ARCHIVED')}</b>
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => handleAction(closeJob)}>
              <b>{t('jobs.CLOSED')}</b>
            </DropdownItem>
          </JobStatusButton>
        );
      case JobStatus.Expired:
        return (
          <JobStatusButton job={job} color="danger" status={t('jobs.EXPIRED')}>
            <DropdownItem onClick={() => handleAction(publishJob)}>
              <b>{t('jobs.PUBLISHED')}</b>
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => handleAction(archiveJob)}>
              <b>{t('jobs.ARCHIVED')}</b>
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => handleAction(closeJob)}>
              <b>{t('jobs.CLOSED')}</b>
            </DropdownItem>
          </JobStatusButton>
        );
      case JobStatus.Archived:
      case JobStatus.Deleted:
        return (
          <JobStatusButton job={job} color="inverse" status={t(`jobs.${jobStatus.toUpperCase()}`)}>
            <DropdownItem onClick={() => handleAction(publishJob)}>
              <b>{t('jobs.PUBLISHED')}</b>
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => handleAction(closeJob)}>
              <b>{t('jobs.CLOSED')}</b>
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => handleAction(archiveJob)}>
              <b>{t('jobs.ARCHIVED')}</b>
            </DropdownItem>
          </JobStatusButton>
        );
      case JobStatus.Closed:
      case JobStatus.Draft:
        return (
          <JobStatusButton
            job={job}
            color={jobStatus === JobStatus.Closed ? 'warning' : 'info'}
            status={t(`jobs.${jobStatus.toUpperCase()}`)}
          >
            <DropdownItem onClick={() => handleAction(publishJob)}>
              <b>{t('jobs.PUBLISHED')}</b>
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => handleAction(archiveJob)}>
              <b>{t('jobs.ARCHIVED')}</b>
            </DropdownItem>
          </JobStatusButton>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <ConfigureEmailModal
        isEmailModalOpen={isConfigureEmailModalOpen}
        toggleIsEmailModalOpen={() => toggleIsConfigureEmailModalOpen()}
        selectedColumn={selectedEmail}
        updateEmailSettings={(id, sendAutomatedEmail, emailSubject, emailContent) =>
          updateCompanyEmailSettingsLocal(company?.id, id, sendAutomatedEmail, emailSubject, emailContent, fetchCompany)
        }
        hideAutomatedSendingOption={true}
      />
      {renderButton()}
    </>
  );
};

export default JobStatusButtons;
